import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import {
  adminAuthors, adminCreateAuthor, adminUpdateAuthor, adminDeleteAuthor, adminUpload, apiError,
} from "@/lib/api";
import { AdminHeader, PrimaryBtn, Modal, fieldCls, labelCls, ImageHint } from "./ui";

const EMPTY = {
  nombre: "", foto: "", biografia_corta: "", biografia_completa: "",
  web: "", instagram: "", facebook: "", youtube: "", otras_redes: "",
  destacado: false, activo: true, seo_title: "", seo_description: "",
};

export default function AdminAuthors() {
  const [authors, setAuthors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => adminAuthors().then(setAuthors);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...EMPTY, ...a }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try { const res = await adminUpload(file); setForm((f) => ({ ...f, foto: res.url })); toast.success("Foto subida."); }
    catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) { await adminUpdateAuthor(editing.id, form); toast.success("Autor actualizado."); }
      else { await adminCreateAuthor(form); toast.success("Autor creado."); }
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };
  const remove = async (a) => { if (!window.confirm(`¿Eliminar a ${a.nombre}?`)) return; await adminDeleteAuthor(a.id); toast.success("Eliminado."); load(); };

  const destacadoCount = authors.filter((a) => a.destacado).length;
  const toggleDestacado = async (a) => {
    const next = !a.destacado;
    if (next && destacadoCount >= 4) return toast.error("Ya hay 4 autores destacados. Quita uno antes de añadir otro.");
    try {
      await adminUpdateAuthor(a.id, { ...a, destacado: next });
      toast.success(next ? "Autor destacado en la página principal." : "Autor quitado de la página principal.");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <AdminHeader title="Autores" subtitle={`${authors.length} autor(es)`} action={<PrimaryBtn onClick={openNew} testId="new-author-btn"><Plus size={15} /> Nuevo autor</PrimaryBtn>} />
      <div className={`mb-5 rounded-lg px-4 py-3 text-sm border ${destacadoCount > 4 ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-[#eef1ff] border-[#1f44e0]/20 text-[#0f2544]"}`} data-testid="destacados-count">
        <Star size={14} className="inline -mt-0.5 mr-1.5 text-[#1f44e0]" />
        Pulsa la estrella de un autor para mostrarlo en la sección «Autores» de la página principal.
        {" "}<strong>{destacadoCount}/4</strong> seleccionados.
        {destacadoCount > 4 && " La página principal mostrará solo los 4 primeros."}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {authors.map((a) => (
          <div key={a.id} className="rounded-xl bg-white border border-[#0f2544]/8 p-4 flex gap-4" data-testid={`author-row-${a.slug}`}>
            <div className="w-14 h-16 rounded bg-[#e7e5df] overflow-hidden shrink-0">{a.foto && <img src={a.foto} alt="" className="w-full h-full object-cover" />}</div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[#0f2544] truncate">{a.nombre}</div>
              <div className="text-xs text-[#8a8a83] flex gap-2 mt-1">
                {a.destacado && <span className="text-[#1f44e0]">Destacado</span>}
                {!a.activo && <span className="text-red-500">Inactivo</span>}
              </div>
              <div className="flex gap-1 mt-2">
                <button onClick={() => toggleDestacado(a)} className={`p-1.5 ${a.destacado ? "text-[#1f44e0]" : "text-[#8a8a83] hover:text-[#1f44e0]"}`} title={a.destacado ? "Quitar de la página principal" : "Destacar en la página principal"} data-testid={`toggle-destacado-${a.slug}`}><Star size={14} fill={a.destacado ? "#1f44e0" : "none"} /></button>
                <button onClick={() => openEdit(a)} className="p-1.5 text-[#8a8a83] hover:text-[#1f44e0]" data-testid={`edit-author-${a.slug}`}><Pencil size={14} /></button>
                <button onClick={() => remove(a)} className="p-1.5 text-[#8a8a83] hover:text-red-600"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar autor" : "Nuevo autor"} wide>
        <form onSubmit={save} className="space-y-4" data-testid="author-form">
          <div><label className={labelCls}>Nombre *</label><input required value={form.nombre} onChange={set("nombre")} className={fieldCls} data-testid="author-nombre" /></div>
          <div>
            <label className={labelCls}>Foto</label>
            <ImageHint>Medida recomendada: <strong>1000 × 1000 px</strong> (cuadrada, relación 1:1). Formato JPG o PNG.</ImageHint>
            <div className="flex items-center gap-3">
              {form.foto && <img src={form.foto} alt="" className="w-12 h-12 object-cover rounded-full border" />}
              <input type="file" accept="image/*" onChange={upload} className="text-sm" data-testid="author-photo-upload" />
              {uploading && <span className="text-xs text-[#8a8a83]">Subiendo...</span>}
            </div>
            <input value={form.foto} onChange={set("foto")} className={`${fieldCls} mt-2`} placeholder="o pega una URL" />
            <p className="text-xs text-[#8a8a83] mt-1">Si pegas un enlace, al guardar copiamos la imagen a nuestro almacenamiento: la web no dependerá del enlace externo.</p>
          </div>
          <div><label className={labelCls}>Biografía corta</label><textarea rows={2} value={form.biografia_corta} onChange={set("biografia_corta")} className={fieldCls} /></div>
          <div><label className={labelCls}>Biografía completa</label><textarea rows={5} value={form.biografia_completa} onChange={set("biografia_completa")} className={fieldCls} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Web</label><input value={form.web} onChange={set("web")} className={fieldCls} /></div>
            <div><label className={labelCls}>Instagram</label><input value={form.instagram} onChange={set("instagram")} className={fieldCls} /></div>
            <div><label className={labelCls}>Facebook</label><input value={form.facebook} onChange={set("facebook")} className={fieldCls} /></div>
            <div><label className={labelCls}>YouTube</label><input value={form.youtube} onChange={set("youtube")} className={fieldCls} /></div>
          </div>
          <div className="flex gap-6 py-1">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.destacado} onChange={set("destacado")} className="accent-[#1f44e0]" data-testid="author-destacado" /> Destacado en la página principal</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.activo} onChange={set("activo")} className="accent-[#1f44e0]" /> Activo</label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2.5 text-sm text-[#8a8a83]">Cancelar</button>
            <PrimaryBtn type="submit" disabled={saving} testId="save-author-btn">{saving ? "Guardando..." : "Guardar"}</PrimaryBtn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
